import { ref } from 'vue'

import type { MacroEditorKeyAction, MacroEditorWheelAction, WheelType } from '../types'

type SingleCaptureMode = 'keyPair' | 'wheel' | 'action'
type SingleCapturePayload = MacroEditorKeyAction | MacroEditorWheelAction

/**
 * 单次录制状态机：
 * - `keyPair` 只在完整收到一组 down/up 后结束，确保编辑/新增按键时成对修改。
 * - `wheel` 收到一次滚轮方向后结束，并根据 enabledWheels 过滤不支持的滚轮方向。
 * - `action` 同时接受按键成对或滚轮，用于编辑时跨类型改写（按键↔滚轮）。
 */
export function useSingleCapture(enabledWheels: WheelType[] = []) {
  const active = ref(false)
  const mode = ref<SingleCaptureMode>('keyPair')
  const callback = ref<((payload: SingleCapturePayload) => void) | null>(null)
  const completeCallback = ref<(() => void) | null>(null)
  const pendingKeystroke = ref<number | null>(null)

  const startSingleCapture = (
    nextMode: SingleCaptureMode,
    nextCallback?: (payload: SingleCapturePayload) => void,
    nextCompleteCallback?: () => void,
  ) => {
    active.value = true
    mode.value = nextMode
    callback.value = nextCallback ?? null
    completeCallback.value = nextCompleteCallback ?? null
    pendingKeystroke.value = null
  }

  const stopSingleCapture = () => {
    active.value = false
    callback.value = null
    completeCallback.value = null
    pendingKeystroke.value = null
  }

  const completeCapture = (payload: SingleCapturePayload) => {
    const current = callback.value
    const currentComplete = completeCallback.value
    stopSingleCapture()

    setTimeout(() => {
      current?.(payload)
      currentComplete?.()
    }, 20)
  }

  const tryCompleteWheel = (payload: SingleCapturePayload) => {
    if (payload.type !== 3) return false

    const wheelPayload = payload as MacroEditorWheelAction
    if (wheelPayload.vWheel !== 0 && !enabledWheels.includes('vertical')) return true
    if (wheelPayload.hWheel !== 0 && !enabledWheels.includes('horizontal')) return true

    completeCapture(payload)
    return true
  }

  const tryConsumeKeyPair = (payload: SingleCapturePayload) => {
    if (payload.type !== 1) return true

    if (payload.action === 1) {
      pendingKeystroke.value = payload.keystroke
      return true
    }

    if (pendingKeystroke.value == null || pendingKeystroke.value !== payload.keystroke) {
      return true
    }

    completeCapture({
      type: 1,
      keystroke: payload.keystroke,
      action: 1,
    })
    return true
  }

  const tryConsumeCapture = (payload: SingleCapturePayload) => {
    if (!active.value) return false

    if (mode.value === 'wheel') {
      if (payload.type !== 3) return true
      return tryCompleteWheel(payload)
    }

    if (mode.value === 'action') {
      if (payload.type === 3) return tryCompleteWheel(payload)
      return tryConsumeKeyPair(payload)
    }

    // keyPair：忽略非按键输入
    if (payload.type !== 1) return true
    return tryConsumeKeyPair(payload)
  }

  return {
    singleCaptureActive: active,
    startSingleCapture,
    stopSingleCapture,
    tryConsumeCapture,
  }
}
